import { Box, Chip, Tooltip, Typography } from '@mui/material';
import DrilldownDialog from './DrilldownDialog';
import { TUTOR_KIND_LABEL, allTutorsUrl, tutorCountTutorsUrl } from './tutorCountTutors';
import { Date1, Mobile, Stack2, digits } from './drilldownCells';

// The popups behind the numbers on the Tutor Count page. Both are one row per tutor, so the rows add up to the number clicked.
//   TutorListDialog - one CRO's tutors ("handling now" or "assigned this month"): the tutor, mobile, the course(s) they teach that
//                     CRO's students, how many of the students, and since when
//   AllTutorsDialog - the company-wide Active Tutors card: every active tutor, the CRO(s) and course(s) / group classes they teach

// the first two names, then "+N more" (a tooltip lists the rest)
const FirstTwo = ({ names, empty = '-' }) => {
  if (!names.length) return <Typography variant="body2" color="text.secondary">{empty}</Typography>;
  const rest = names.slice(2);
  return (
    <Box>
      {names.slice(0, 2).map((n, i) => <Typography key={i} variant="body2" fontWeight={i === 0 ? 600 : 400}>{n}</Typography>)}
      {rest.length > 0 && <Tooltip title={rest.join('\n')}><Chip size="small" label={`+${rest.length} more`} sx={{ mt: 0.25 }} /></Tooltip>}
    </Box>
  );
};

const tutorCell = { key: 'name', label: 'Tutor', sort: (r) => (r.name || '').toLowerCase(), render: (r) => <Stack2 top={r.name} bottom={r.code} />, csv: (r) => r.name };
const mobileCell = { key: 'mobile', label: 'Mobile', sort: (r) => digits(r.mobile), render: (r) => <Mobile value={r.mobile} />, csv: (r) => r.mobile };
const num = (key, label, field) => ({ key, label, align: 'right', sort: (r) => r[field], render: (r) => <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r[field]}</Typography>, csv: (r) => r[field] });

const CRO_COLUMNS = (kind) => [
  tutorCell,
  mobileCell,
  { key: 'courses', label: 'Course(s)', sort: (r) => ((r.courses || [])[0]?.course || '').toLowerCase(), render: (r) => <FirstTwo names={(r.courses || []).map((c) => c.course)} />, csv: (r) => (r.courses || []).map((c) => c.course).join(' / ') },
  { key: 'program', label: 'Program', sort: (r) => ((r.courses || [])[0]?.program || '').toLowerCase(), render: (r) => <Typography variant="body2">{[...new Set((r.courses || []).map((c) => c.program).filter(Boolean))].join(', ') || '-'}</Typography>, csv: (r) => [...new Set((r.courses || []).map((c) => c.program).filter(Boolean))].join(' / ') },
  num('students', 'Students', 'studentsCount'),
  { key: 'assignedOn', label: kind === 'new' ? 'Assigned on' : 'With this CRO since', sort: (r) => r.assignedOn || '', render: (r) => <Date1 value={r.assignedOn} dateOnly />, csv: (r) => r.assignedOn },
  { key: 'cro', label: 'CRO', sort: (r) => (r.cro || '').toLowerCase(), render: (r) => <Typography variant="body2">{r.cro || '-'}</Typography>, csv: (r) => r.cro },
];

const ALL_COLUMNS = [
  tutorCell,
  mobileCell,
  { key: 'cros', label: 'CRO(s)', sort: (r) => (r.cros?.[0] || '').toLowerCase(), render: (r) => <FirstTwo names={r.cros || []} empty="Group classes only" />, csv: (r) => (r.cros || []).join(' / ') },
  {
    key: 'courses', label: 'Course(s) / group classes',
    sort: (r) => ((r.courses || [])[0]?.course || (r.groupClasses || [])[0] || '').toLowerCase(),
    render: (r) => <FirstTwo names={[...(r.courses || []).map((c) => c.course), ...(r.groupClasses || []).map((g) => `Group: ${g}`)]} />,
    csv: (r) => [...(r.courses || []).map((c) => c.course), ...(r.groupClasses || []).map((g) => `Group: ${g}`)].join(' / '),
  },
  num('students', 'Students', 'studentsCount'),
  num('assignments', 'Assignments', 'assignmentsCount'),
  { key: 'assignedOn', label: 'First assigned', sort: (r) => r.assignedOn || '', render: (r) => <Date1 value={r.assignedOn} dateOnly />, csv: (r) => r.assignedOn },
];

const searchTextCro = (r) => [r.name, r.code, digits(r.mobile), r.mobile, ...(r.courses || []).flatMap((c) => [c.course, c.program])].filter(Boolean).join(' ').toLowerCase();
const searchTextAll = (r) => [r.name, r.code, digits(r.mobile), r.mobile, ...(r.cros || []), ...(r.groupClasses || []), ...(r.courses || []).flatMap((c) => [c.course, c.program])].filter(Boolean).join(' ').toLowerCase();

export const TutorListDialog = ({ request, onClose }) => {
  const { kind, userName, month } = request;
  return (
    <DrilldownDialog
      url={tutorCountTutorsUrl(request)}
      title={TUTOR_KIND_LABEL[kind]}
      chips={[userName, month.format('MMMM YYYY')]}
      columns={CRO_COLUMNS(kind)}
      searchText={searchTextCro}
      searchLabel="Search these tutors"
      searchPlaceholder="Search tutor, code, mobile, course"
      emptyText="No tutors."
      rowKey={(r) => r.trainerId}
      csvName={`${TUTOR_KIND_LABEL[kind].replace(/ /g, '_')}_${(userName || 'CRO').replace(/\W+/g, '_')}_${month.format('YYYY-MM')}.csv`}
      onClose={onClose}
    />
  );
};

export const AllTutorsDialog = ({ request, onClose }) => {
  const { month } = request;
  return (
    <DrilldownDialog
      url={allTutorsUrl(request)}
      title="Active tutors"
      chips={[month.format('MMMM YYYY')]}
      dataChips={(d) => [d.scoped ? 'Your CROs only' : null]}
      columns={ALL_COLUMNS}
      searchText={searchTextAll}
      searchLabel="Search all active tutors"
      searchPlaceholder="Search tutor, code, mobile, CRO, course"
      emptyText="No active tutors."
      rowKey={(r) => r.trainerId}
      csvName={`Active_tutors_${month.format('YYYY-MM')}.csv`}
      onClose={onClose}
    />
  );
};
